/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
 * document.addEventListener(ThemeEvents.variantUpdate, (e) => { console.log(e.detail.variant) });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when an option is selected */
  static optionSelected = 'option:selected';
  /** @static @constant {string} Event triggered when a variant is changed */
  static variantUpdated = 'variant:updated';
  /** @static @constant {string} Event triggered when a slider is changed */
  static sliderChanged = 'slider:changed';
  /** @static @constant {string} Event triggered as soon as a slider starts moving to a new position */
  static sliderChanging = 'slider:changing';
  /** @static @constant {string} Event triggered when a cart is updated */
  static cartUpdated = 'cart:updated';
   /** @static @constant {string} Event triggered when a cart is updated */
  static cartQuantityUpdated = 'cartquantity:updated';
  /** @static @constant {string} Event triggered when a cart is updated */
  static cartFormSubmitted = 'cart:form:submitted';
  /** @static @constant {string} Event triggered when a cart is errored */
  static cartErrored = 'cart:errored';
  /** @static @constant {string} Event triggered when a query is updated */
  static queryUpdated = 'predictivesearchquery:updated';
  /** @static @constant {string} Event triggered when search results are updated */
  static resultsUpdated = 'predictivesearchresults:updated';
  /** @static @constant {string} Event triggered when the visitor's geolocation is resolved */
  static geolocationLoaded = 'geolocation:loaded';
  /** @static @constant {string} Event triggered when the mobile filter drawer is opened */
  static filterOpened = 'filter:opened';
  /** @static @constant {string} Event triggered when the age gate is shown */
  static ageGateOpened = 'agegate:opened';
  /** @static @constant {string} Event triggered when the age gate is answered */
  static ageGateAnswered = 'agegate:answered';
}

/**
 * Event fired when a variant is selected
 * @extends {Event}
 */
export class OptionSelectedEvent extends Event {
  /**
   * Creates a new OptionSelectedEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   * @param {Object} [resource.option] - The changed option input
   * @param {string} [resource.option.name] - The option's input name
   * @param {string} [resource.option.value] - The selected value, e.g. "Black"
   */
  constructor(resource) {
    super(ThemeEvents.optionSelected, { bubbles: true });
    this.detail = {
      variant: resource?.variant,
      productId: resource?.productId,
      formElement: resource?.formElement,
      option: resource?.option,
    };
  }
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class VariantUpdatedEvent extends Event {
  /**
   * Creates a new VariantUpdateEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   * @param {boolean} resource.available - Whether the variant is available
   * @param {boolean} resource.inventory_management - Whether the variant has inventory management
   * @param {Object} [resource.featured_media] - The featured media of the variant
   * @param {string} [resource.featured_media.id] - The id of the featured media
   * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
   * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {Document} data.html - The new document fragment for the variant
   * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
   * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
   * @param {string} data.newProduct.id - The id of the new product
   * @param {string} data.newProduct.url - The url of the new product
   */
  constructor(resource) {
    super(ThemeEvents.variantUpdated, { bubbles: true });
    this.detail = {
      variant: resource?.variant,
      productId: resource?.productId,
      formElement: resource?.formElement,
    };
  }
}

export class SliderChangedEvent extends Event {
  /**
   * Creates a new SliderChangedEvent
   * @param {Object} resource - The changed slider
   * @param {string} resource.id - The slider container id
   * @param {number} resource.position - The new slide position
   * @param {string} [resource.direction] - What moved the slider: "prev", "next" or
   *   "thumbnail"; undefined for swipes and programmatic scrolls
   */
  constructor(resource) {
    super(ThemeEvents.sliderChanged, { bubbles: true });
    this.detail = {
      id: resource?.id,
      position: resource?.position,
      direction: resource?.direction,
    };
  }
}

/**
 * Event fired as soon as a slider is heading for a new position: at the start of a
 * programmatic scroll (arrows, scrollToPosition) or when a swipe passes a slide's
 * midpoint. It fires before the scroll settles; SliderChangedEvent follows once it has.
 * @extends {Event}
 */
export class SliderChangingEvent extends Event {
  /**
   * Creates a new SliderChangingEvent
   * @param {Object} resource - The moving slider
   * @param {string} resource.id - The slider container id
   * @param {number} resource.position - The slide position the slider is moving to
   */
  constructor(resource) {
    super(ThemeEvents.sliderChanging, { bubbles: true });
    this.detail = {
      id: resource?.id,
      position: resource?.position,
    };
  }
}

/**
 * Event class for cart updates
 * @extends {Event}
 */
export class CartUpdatedEvent extends Event {
  /**
   * Creates a new CartUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was updated
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdated, { bubbles: true });
    this.detail = {
      cartItems: resource?.cartItems,
      cartSummary: resource?.cartSummary,
      itemCount: resource?.itemCount
    };
  }
}

export class CartQuantityUpdatedEvent extends Event {
  /**
   * Creates a new CartQuantityUpdatedEvent
   */
  constructor(resource) {
    super(ThemeEvents.cartQuantityUpdated, { bubbles: true });
    this.detail = {
      variantId: resource?.variantId,
      lineId: resource?.lineId,
      line: resource?.line,
      quantity: resource?.quantity,
      key: resource?.key,
      hasBonus: resource?.hasBonus,
      lineItemProperties: resource?.lineItemProperties,
    };
  }
}

export class CartFormSubmittedEvent extends Event {
  /**
   * Creates a new CartFormSubmittedEvent
   */
  constructor(resource) {
    super(ThemeEvents.cartFormSubmitted, { bubbles: true });
    this.detail = {
      formElement: resource?.formElement,
    };
  }
}

export class CartErroredEvent extends Event {
  /**
   * Creates a new CartErroredEvent
   */
  constructor(resource) {
    super(ThemeEvents.cartErrored, { bubbles: true });
    this.detail = {
      type: resource?.type,
      errorMessage: resource?.errorMessage,
      lineId: resource?.lineId,
    };
  }
}

export class QueryUpdatedEvent extends Event {
  /**
   * Creates a new QueryUpdatedEvent
   */
  constructor(resource) {
    super(ThemeEvents.queryUpdated, { bubbles: true });
    this.detail = {
      query: resource?.query,
    };
  }
}

/**
 * Event fired when the visitor's geolocation is resolved
 * @extends {Event}
 */
export class GeolocationLoadedEvent extends Event {
  /**
   * Creates a new GeolocationLoadedEvent
   * @param {Object} resource - The geolocation object
   * @param {string} resource.country - The visitor's country code
   * @param {string} resource.region - The visitor's region
   */
  constructor(resource) {
    super(ThemeEvents.geolocationLoaded, { bubbles: true });
    this.detail = {
      country: resource?.country,
      region: resource?.region,
    };
  }
}

/**
 * Event fired when the mobile filter drawer is opened
 * @extends {Event}
 */
export class FilterOpenedEvent extends Event {
  /**
   * Creates a new FilterOpenedEvent
   */
  constructor() {
    super(ThemeEvents.filterOpened, { bubbles: true });
  }
}

/**
 * Event fired when the age gate is shown to the visitor
 * @extends {Event}
 */
export class AgeGateOpenedEvent extends Event {
  /**
   * Creates a new AgeGateOpenedEvent
   */
  constructor() {
    super(ThemeEvents.ageGateOpened, { bubbles: true });
  }
}

/**
 * Event fired when the visitor answers the age gate
 * @extends {Event}
 */
export class AgeGateAnsweredEvent extends Event {
  /**
   * Creates a new AgeGateAnsweredEvent
   * @param {Object} resource - The given answer
   * @param {string} resource.answer - "Yes" or "No"
   */
  constructor(resource) {
    super(ThemeEvents.ageGateAnswered, { bubbles: true });
    this.detail = {
      answer: resource?.answer,
    };
  }
}

export class ResultsUpdatedEvent extends Event {
  /**
   * Creates a new ResultsUpdatedEvent
   */
  constructor(resource) {
    super(ThemeEvents.resultsUpdated, { bubbles: true });
    this.detail = {
      results: resource?.results,
    };
  }
}